/**
 * QrLoginDrawerBlock - QR code for "log in on this device with your phone".
 * Backend returns a one-time code; we draw the QR client-side with qrcode.react (no third-party QR service).
 * Desktop listens for the code to be claimed, then signs in with custom token and redirects to dashboard.
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { firebaseApp, db } from "@healthdesk/shared-services";

const AUTO_REFRESH_MS = 4 * 60 * 1000;

const QrLoginDrawerBlock = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const unsubRef = useRef(null);

  // Create code via HTTP endpoint (createQrLoginCodeHttp) for all origins. It sends CORS and avoids callable 500s.
  useEffect(() => {
    let cancelled = false;

    const createCode = async () => {
      setLoading(true);
      setError(null);
      try {
        const projectId =
          firebaseApp.options?.projectId || (typeof process !== "undefined" && process.env?.REACT_APP_FIREBASE_PROJECT_ID);
        const url = projectId
          ? `https://us-central1-${projectId}.cloudfunctions.net/createQrLoginCodeHttp`
          : null;
        if (!url) throw new Error("Missing project ID");
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
          cache: "no-store",
        });
        if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
        const data = await res.json();
        if (!cancelled && data?.code) {
          setCode(data.code);
        }
      } catch (e) {
        if (!cancelled) {
          const errCode = e?.code;
          const msg = (e?.message || "").trim();
          const isInternalOrCors =
            msg.includes("CORS") ||
            msg.includes("Failed to fetch") ||
            msg === "INTERNAL" ||
            msg.toUpperCase() === "INTERNAL" ||
            errCode === "internal";
          const origin = typeof window !== "undefined" ? window.location.origin : "";
          const isDemoSite = origin.includes("hlthdsk-experimental-demo.web.app");
          const friendly = isInternalOrCors
            ? isDemoSite
              ? "QR login is temporarily unavailable. Please try again in a moment or sign in with your phone number."
              : "QR code unavailable from this origin. Try the demo site (https://hlthdsk-experimental-demo.web.app) or run the app and Functions from the same host."
            : msg || "Failed to create login code";
          setError(friendly);
          console.warn("[QrLoginDrawerBlock] createQrLoginCode failed", e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    createCode();
    return () => {
      cancelled = true;
    };
  }, [retryCount]);

  // Rotate QR code before backend TTL so users don't scan a stale code.
  useEffect(() => {
    if (!code) return undefined;
    const timer = window.setTimeout(() => {
      setRetryCount((c) => c + 1);
    }, AUTO_REFRESH_MS);
    return () => window.clearTimeout(timer);
  }, [code]);

  // Subscribe to code doc; when claimed, sign in and redirect
  useEffect(() => {
    if (!code) return;

    const docRef = db.collection("qrLoginCodes").doc(code);
    const unsub = docRef.onSnapshot(
      (snap) => {
        const data = snap.data();
        if (!data || data.status !== "claimed" || !data.customToken) return;

        if (unsubRef.current) unsubRef.current();
        unsubRef.current = null;

        // Desktop signs in and goes to dashboard (role-based default); phone stays on claim success page.
        firebaseApp
          .auth()
          .signInWithCustomToken(data.customToken)
          .then(() => {
            navigate("/dashboard", { replace: true });
          })
          .catch((e) => {
            console.warn("[QrLoginDrawerBlock] signInWithCustomToken failed", e);
            setError("Login failed. Please try again.");
          });
      },
      (err) => {
        console.warn("[QrLoginDrawerBlock] snapshot error", err);
      }
    );

    unsubRef.current = unsub;
    return () => {
      if (unsubRef.current) unsubRef.current();
      unsubRef.current = null;
    };
  }, [code, navigate]);

  if (loading) {
    return (
      <Box sx={{ py: 2, px: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
        <CircularProgress size={24} />
        <Typography variant="caption" color="text.secondary">
          Preparing QR code…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 2, px: 2 }}>
        <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
          {error}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => { setError(null); setLoading(true); setRetryCount((c) => c + 1); }}>
          Try again
        </Button>
      </Box>
    );
  }

  if (!code) return null;

  const loginUrl =
    (typeof window !== "undefined" ? window.location.origin : "") + "/auth/qr-login?code=" + encodeURIComponent(code);

  return (
    <Box sx={{ width: "100%", py: 2, px: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", boxSizing: "border-box" }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textAlign: "center" }}>
        If your mobile device is already authenticated, you can scan this to log on to this device with your phone.
      </Typography>
      <Box sx={{ bgcolor: "common.white", p: 1, borderRadius: 1, display: "inline-flex", flexShrink: 0, lineHeight: 0 }} role="img" aria-label="QR code for phone login">
        <QRCodeCanvas value={loginUrl} size={140} level="M" />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
        Scan with your phone to sign in
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: "center" }}>
        Code refreshes automatically.
      </Typography>
    </Box>
  );
};

export default QrLoginDrawerBlock;
